import React, { createContext, PropsWithChildren, useContext, useMemo } from 'react';
import { useFetchAppMarketPermissionsQuery } from '../../redux/api/appMarket-api-slice';
import { AppMarketPermission } from '../../models/app';
import { EntityState } from '@reduxjs/toolkit';
import { useAppContext } from '../../hooks/useAppContext';

type PermissionsProviderType = {
  isLoading: boolean;
  data: Maybe<EntityState<AppMarketPermission>>;
}

let defaultResult: EntityState<AppMarketPermission> = {
  ids: [],
  entities: {},
}

const defaultValue = {
  isLoading: false,
  data: defaultResult,
};

export const PermissionsContext = createContext<PermissionsProviderType>(defaultValue);
PermissionsContext.displayName = 'Permissions';

export const usePermissionContext = (): PermissionsProviderType => useContext(PermissionsContext);

export const PermissionsProvider: React.FC<PropsWithChildren<{}>> = (props: PropsWithChildren<{}>): JSX.Element => {
  const { children } = props;
  const { id: contextId } = useAppContext(); // will be '' incase of publicApp mode
  const skipCall = !contextId; // we can skip the api call if there is no context(meaning public mode)

  const {
    data: appMarketPermissions,
    isLoading: permissionsLoading,
  } = useFetchAppMarketPermissionsQuery(contextId, { skip: skipCall }); //should be skipped in loggedOut mode

  const value = useMemo(() => {
    return {
      isLoading: permissionsLoading,
      data: skipCall ? defaultResult : appMarketPermissions,
    };
  }, [appMarketPermissions, permissionsLoading, skipCall]);

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
};
